import {HttpErrorResponse} from '@angular/common/http';
import {Component, DestroyRef, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {finalize, switchMap} from 'rxjs';
import {NzSpinComponent} from 'ng-zorro-antd/spin';
import {NZ_MODAL_DATA, NzModalRef} from 'ng-zorro-antd/modal';
import {NzNotificationService} from 'ng-zorro-antd/notification';
import {TranslateService} from '@ngx-translate/core';
import {AuthFlowService, ConsentDocument} from '../../../core/services/auth-flow.service';
import {ConsentView} from '../../../shared/consent-view/consent-view';
import {NcalayerService} from '../../../core/services/ncalayer.service';
import {NcalayerCancelledError} from '../../../core/services/ncalayer-cancelled-error';
import {NcalayerUnavailableError} from '../../../core/services/ncalayer-unavailable-error';

@Component({
  selector: 'app-consent-modal',
  imports: [
    NzSpinComponent,
    ConsentView,
  ],
  templateUrl: './consent-modal.html',
})
export class ConsentModal {
  private readonly _modalRef = inject(NzModalRef);
  private readonly _data = inject<{consentToken?: string}>(NZ_MODAL_DATA, {optional: true});
  private readonly _authFlow = inject(AuthFlowService);
  private readonly _ncalayer = inject(NcalayerService);
  private readonly _notification = inject(NzNotificationService);
  private readonly _translate = inject(TranslateService);
  private readonly _destroyRef = inject(DestroyRef);

  readonly document = signal<ConsentDocument | null>(null);
  readonly loading = signal(true);

  constructor() {
    this._modalRef.updateConfig({nzOkDisabled: true});

    this._authFlow.consentDocument()
      .pipe(finalize(() => this.loading.set(false)), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (document) => {
          this.document.set(document);
          this._modalRef.updateConfig({nzOkDisabled: false});
        },
        error: () => this._notification.error(
          this._translate.instant('consent.errorTitle'),
          this._translate.instant('consent.loadFailed')
        ),
      });
  }

  handleOk(): void {
    const document = this.document();
    if (!document) return;

    this._modalRef.updateConfig({nzOkLoading: true, nzCancelDisabled: true});

    this._ncalayer.signXml(document.xml)
      .pipe(
        switchMap((signedXml) => this._authFlow.acceptConsent({xml: signedXml, consentToken: this._data?.consentToken})),
        finalize(() => this._modalRef.updateConfig({nzOkLoading: false})),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: () => {
          this._modalRef.close(true);
          this._notification.success(
            this._translate.instant('consent.acceptedTitle'),
            this._translate.instant('consent.acceptedDesc')
          );
        },
        error: (err) => {
          this._modalRef.updateConfig({nzCancelDisabled: false});

          if (err instanceof NcalayerCancelledError) {
            this._notification.warning(
              this._translate.instant('ncalayer.cancelledTitle'),
              this._translate.instant('ncalayer.cancelledDesc')
            );
            return;
          }

          if (err instanceof NcalayerUnavailableError) {
            this._notification.warning(
              this._translate.instant('ncalayer.unavailableTitle'),
              this._translate.instant('ncalayer.unavailableDesc')
            );
            return;
          }

          if (err instanceof HttpErrorResponse) return;

          this._notification.error(
            this._translate.instant('consent.errorTitle'),
            err?.message || this._translate.instant('consent.failed')
          );
        }
      });
  }
}
